<template>
  <div class="projects">
    <section class="section">
      <div class="container">
        <div class="columns">
          <div class="column column is-full">
            <div class="box">
              <div class="columns" style="margin-top: 16px">
                <div class="column">
                  <b-dropdown v-if="currentUserAccess('Projects', this.UserActions.CREATE)" :triggers="['hover']"
                              aria-role="list">
                    <template #trigger>
                      <b-button class="button is-primary" icon-left="plus-thick">
                        {{ $t('projects-view.new-project-button') }}
                      </b-button>
                    </template>
                    <b-dropdown-item aria-role="listitem" @click="createNewProjectClicked">
                      {{ $t('projects-view.create-new-project') }}
                    </b-dropdown-item>
                    <b-dropdown-item aria-role="listitem" @click="createFromTemplateClicked">
                      {{ $t('projects-view.create-from-template') }}
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
                <div class="column is-narrow">
                  <b-field grouped group-multiline>
                    <b-select v-model="selectedPhase"
                              :placeholder="$t('project-details-view.project-status-placeholder')">
                      <option
                          v-for="option in projectsPhases"
                          :value="option.id"
                          :key="option.id">
                        {{ $t(option.translationKey) }}
                      </option>
                    </b-select>
                    <div class="control is-flex">
                      <b-switch v-model="isOnlyArchived">{{ $t('projects-view.show-archived-projects-filter') }}</b-switch>
                    </div>
                  </b-field>
                </div>
              </div>
              <b-table class="projects-table" :data="projectsData" :striped="true" :hoverable="false"
                       :mobile-cards="true" :loading="isLoading" :scrollable="true">

                <b-table-column field="project.code" :label="this.$t('projects-view.project-code-column')"
                                v-slot="props" width="12%" sortable searchable>
                  <a :href="projectDetailsLink(props.row.project.document_id)">{{ props.row.project.code }}</a>
                </b-table-column>
                <b-table-column field="project.title" :label="this.$t('projects-view.project-title-column')"
                                v-slot="props" width="16%" sortable searchable>
                  {{ props.row.project.title }}
                </b-table-column>
                <b-table-column field="manager_names" :label="this.$t('projects-view.project-managers-column')"
                                v-slot="props">
                  <b-taglist>
                    <b-tag type="is-info is-light" size="is-small"
                           v-for="projectManager in props.row.project.meta.managers"
                           v-bind:key="projectManager.name.first">
                      {{ getFullName(projectManager.name) }}
                    </b-tag>
                  </b-taglist>
                </b-table-column>

                <b-table-column field="site_manager_names" :label="this.$t('projects-view.site-managers-column')"
                                v-slot="props">
                  <b-taglist>
                    <b-tag type="is-info is-light" size="is-small"
                           v-for="siteManager in props.row.project.meta.site_managers"
                           v-bind:key="siteManager.name.first">
                      {{ getFullName(siteManager.name) }}
                    </b-tag>
                  </b-taglist>
                </b-table-column>

                <b-table-column field="project.phase" :label="this.$t('projects-view.status-column')" v-slot="props" sortable>
                  {{
                    $t(projectPhaseOptions.find(obj => {
                      return obj.id === props.row.project.phase
                    }).translationKey)
                  }}
                </b-table-column>

                <b-table-column :visible="isOnlyArchived" field="is_archived"
                                :label="this.$t('projects-view.archived-column')" v-slot="props"
                                width="5%" centered>
                  <b-icon v-if="props.row.project.is_archived"
                          icon="check"
                          type="is-primary">
                  </b-icon>
                </b-table-column>

                <b-table-column v-slot="props" cellClass="has-text-right">
                  <b-button v-if="currentUserAccess('ProjectSummary', UserActions.VIEW, props.row.project.document_id)"
                            class="button is-primary is-small m-1" tag="router-link"
                            :to="projectSummaryLink(props.row.project.document_id)">
                    {{ $t('projects-view.show-summary') }}
                  </b-button>
                  <b-button v-if="currentUserAccess('ProjectLogs', UserActions.VIEW, props.row.project.document_id)"
                            class="button is-primary is-small m-1" tag="router-link"
                            :to="projectWorklogsLink(props.row.project.document_id)"
                            style="margin-left: 8px">
                    {{ $t('projects-view.enter-worklog') }}
                  </b-button>
                </b-table-column>

              </b-table>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import firebase from "firebase/app";

export default {
  name: "Projects",
  data() {
    return {
      projectsData: [],
      userRole: {},
      isLoading: false,
      isOnlyArchived: false,
      selectedPhase: "all"
    }
  },
  watch: {
    isOnlyArchived: function () {
      this.readProjectsData();
    },
    selectedPhase: function () {
      this.readProjectsData();
    }
  },
  methods: {
    readProjectsData: function () {
      this.isLoading = true
      this.projectsData = []
      const functions = firebase.app().functions("europe-west3");
      const getProjectsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payload = {
        action: "get_projects", fill_meta: true,
        filters: [['is_archived', '==', this.isOnlyArchived]]
      }
      if (this.selectedPhase !== 'all') {
        payload.filters.push(['phase', '==', this.selectedPhase])
      }
      getProjectsFunction(payload)
          .then((result) => {
            console.log("result: ", result.data);
            result.data["projects"].forEach((project) => {
              this.projectsData.push({
                project: project
              });
            });
            this.isLoading = false
          })
          .catch((error) => {
            this.showError(error.message)
            console.log("Error getting projects: ", error);
            this.isLoading = false;
          });
    },
    projectDetailsLink: function (projectId) {
      if (this.currentUserAccess("ProjectDetails", this.UserActions.VIEW, projectId))
        return "#/projects/" + projectId;
      else
        return "#/projects/";
    },
    projectWorklogsLink: function (projectId) {
      return "/projects/" + projectId + "/worklogs"
    },
    projectSummaryLink: function (projectId) {
      return "/projects/" + projectId + "/summary"
    },
    createNewProjectClicked: function () {
      this.$router.push({
        name: 'ProjectDetails',
        params: {
          id: "new"
        }
      })
    },
    createFromTemplateClicked: function () {
      this.$router.push({
        name: 'Templates'
      })
    }
  },
  computed: {
    projectsPhases() {
      let allProjectPhases = this.projectPhaseOptions.slice();
      allProjectPhases.unshift({
        id: "all",
        translationKey: "enums.project-phase.all"
      })
      return allProjectPhases
    }
  },
  mounted() {
    if (!this.currentUserAccess("Projects", this.UserActions.VIEW)) {
      this.$router.push({
        name: 'UserDetails',
        params: {
          id: this.getLocalUserId()
        }
      })
    }
    // load user details from the server on each projects list refresh.
    this.loadUserRoleFromServer();
    this.readProjectsData();
  }
};
</script>

<style scoped>
.projects-table {
  margin-top: 16px;
}
</style>
